import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { PRESEASON_WEEK } from '@ff/core';
import { fetchUnread, markTalkRead, type UnreadBoard } from './api.ts';

/** How often the badge asks. The boards are cached server-side, so this is cheap. */
const POLL_MS = 60_000;

/** A board with the season it belongs to, so a mark can find it without a second lookup. */
type SeasonBoard = UnreadBoard & { season: number };

interface Unread {
  season: number | null;
  boards: SeasonBoard[];
  /** Unread on the game-week boards, for the Scoreboard tab. */
  scoreboardTotal: number;
  /** Unread on the War Room board, until kickoff. */
  warRoomTotal: number;
  /** Tell the server the member has seen a board up to its newest post. */
  markRead: (season: number, week: number, upTo: string) => void;
  refresh: () => void;
}

const Ctx = createContext<Unread | null>(null);

/**
 * The unread counts behind the nav badge. Polls on its own clock, not the
 * season's (which stops between game windows), skips ticks while the tab is
 * hidden, and refreshes the moment it comes back.
 *
 * Marking read is optimistic and monotonic: the count zeroes at once, and a
 * mark that isn't newer than what we already sent is dropped, so a board that
 * re-renders every fifteen seconds writes only when something new was seen.
 */
export function UnreadProvider({ children }: { children: ReactNode }) {
  const [season, setSeason] = useState<number | null>(null);
  const [boards, setBoards] = useState<SeasonBoard[]>([]);
  const sent = useRef<Record<string, string>>({});

  const refresh = useCallback(() => {
    fetchUnread()
      .then((r) => {
        setSeason(r.season);
        setBoards(
          r.boards.map((b) => {
            const key = `${r.season}_${b.week}`;
            const mine = sent.current[key];
            if (b.readAt && (mine ?? '') < b.readAt) sent.current[key] = b.readAt;
            // A board we've already marked past what the server counted from
            // is read, whatever the server said; its write is on its way.
            const read = mine && (!b.readAt || mine > b.readAt);
            return { ...b, season: r.season, ...(read ? { count: 0, readAt: mine } : {}) };
          }),
        );
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    refresh();
    const tick = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const id = setInterval(tick, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  // Keyed by the board itself, not by the provider's current season: on first
  // load the board can render before the unread response lands, and a mark
  // compared against a season we don't know yet would clear nothing.
  const markRead = useCallback(
    (s: number, week: number, upTo: string) => {
      const key = `${s}_${week}`;
      if ((sent.current[key] ?? '') >= upTo) return;
      const previous = sent.current[key];
      sent.current[key] = upTo;
      setBoards((cur) =>
        cur.map((b) => (b.season === s && b.week === week ? { ...b, count: 0, readAt: upTo } : b)),
      );
      void markTalkRead(s, week, upTo).catch(() => {
        // The server never heard it. Forget we sent it, so the next board
        // render sends it again, and show the server's count meanwhile.
        if (sent.current[key] === upTo) {
          if (previous) sent.current[key] = previous;
          else delete sent.current[key];
        }
        refresh();
      });
    },
    [refresh],
  );

  const value = useMemo<Unread>(() => {
    const sum = (pick: (b: UnreadBoard) => boolean) =>
      boards.filter(pick).reduce((n, b) => n + b.count, 0);
    return {
      season,
      boards,
      scoreboardTotal: sum((b) => b.week !== PRESEASON_WEEK),
      warRoomTotal: sum((b) => b.week === PRESEASON_WEEK),
      markRead,
      refresh,
    };
  }, [season, boards, markRead, refresh]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

const NONE: Unread = {
  season: null,
  boards: [],
  scoreboardTotal: 0,
  warRoomTotal: 0,
  markRead: () => undefined,
  refresh: () => undefined,
};

/** Safe outside the provider (returns zeros), so components render the same in previews. */
export function useUnread(): Unread {
  return useContext(Ctx) ?? NONE;
}
